// Who may give which role (access-control §3, users-and-roles UR-13): the screens hide what a role cannot do, and
// vision-api enforces it (organisation-admin plan, risks). A refusal from the API is always shown in words.
import type { components } from "../../api/generated/vision-api";
import { ApiError } from "../../api/problem";

export type Role = components["schemas"]["Role"];
type Membership = components["schemas"]["Membership"];
type T = (key: string, options?: Record<string, unknown>) => string;

/** Highest first, as the role picker lists them. */
export const ROLES: Role[] = ["owner", "admin", "manager", "operator", "viewer", "guest"];

/** Roles whose ceiling holds `org.admin` (access-control §3). */
export function isAdminRole(role: Role | undefined): boolean {
  return role === "owner" || role === "admin";
}

/**
 * The Account section is for Owners and Admins (WEB-ORG-01), of a personal account too: its Owner invites people and
 * makes places straight away (accounts-places-sharing CL-01, replacing WEB-ORG-02).
 */
export function hasOrganisationSection(m: Membership | undefined): boolean {
  return !!m && isAdminRole(m.role);
}

/**
 * The roles an actor may give, in a role change or an invitation (WEB-MEM-02, -04): only an Owner gives Owner, and
 * Guest is never given here; a person becomes a Guest when something is shared with them (UR-12).
 */
export function assignableRoles(actor: Role | undefined): Role[] {
  if (!isAdminRole(actor)) return [];
  return ROLES.filter((r) => r !== "guest" && (r !== "owner" || actor === "owner"));
}

/**
 * Whether the actor may change this member's role from the list: an Admin cannot touch an Owner, only an Owner
 * promotes a Guest (UR-13), and nobody changes their own role here (the last-Owner rule is the API's to say).
 */
export function canChangeRole(actor: Role | undefined, target: Role, self: boolean): boolean {
  if (self || !isAdminRole(actor)) return false;
  if (target === "owner" || target === "guest") return actor === "owner";
  return true;
}

/** Whether the actor may remove this member: only an Owner removes an Owner, and never yourself (WEB-MEM-03). */
export function canRemove(actor: Role | undefined, target: Role, self: boolean): boolean {
  if (self || !isAdminRole(actor)) return false;
  return target !== "owner" || actor === "owner";
}

/** An invitation past its end can no longer be accepted (WEB-MEM-05). */
export function isExpired(expiresAt: string, now: Date): boolean {
  return Date.parse(expiresAt) <= now.getTime();
}

/** A refused change in words: the last-Owner rule, a refusal by role, or the API's own message. */
export function memberError(error: unknown, t: T): string {
  if (!(error instanceof ApiError)) return t("organisation.error.unknown");
  if (error.code === "last_owner") return t("organisation.error.lastOwner");
  if (error.status === 403) return t("organisation.error.forbidden");
  if (error.status === 409) return t("organisation.error.conflict", { detail: error.message });
  return error.message;
}

export type GrantPermission = components["schemas"]["GrantPermission"];

/** What a grant can hold, in the order the picker shows them. */
export const GRANT_PERMISSIONS: GrantPermission[] = [
  "view",
  "control",
  "automate",
  "configure",
  "manage",
  "share",
  "export",
];

/** Each role's ceiling (access-control §3): a grant to a member holds nothing above it (WEB-GRT-02). */
const CEILING: Record<Role, GrantPermission[]> = {
  owner: GRANT_PERMISSIONS,
  admin: GRANT_PERMISSIONS,
  manager: GRANT_PERMISSIONS,
  operator: ["view", "control", "automate"],
  viewer: ["view", "export"],
  guest: ["view", "control"],
};

export function ceiling(role: Role): GrantPermission[] {
  return CEILING[role];
}

/** A grant ends at the end of the chosen day, in the browser's time zone; "" means it does not end. */
export function endOfDay(date: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  const [y, m, d] = date.split("-").map(Number);
  return new Date(y ?? 0, (m ?? 1) - 1, d ?? 1, 23, 59, 59).toISOString();
}

/** The date input's value for a grant's end, in the browser's time zone. */
export function dayOf(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** A refused grant in words: what the actor lacks (the delegation rule), or the API's own message. */
export function grantError(error: unknown, t: T): string {
  if (error instanceof ApiError && error.status === 403)
    return t("organisation.grants.refused", { detail: error.message });
  if (error instanceof ApiError && error.status === 409) return t("organisation.grants.roleGrant");
  return memberError(error, t);
}
